import { useState, useEffect } from "react";
import styles from "./index.module.css";
import { useDispatch, useSelector } from "react-redux";
import { PulseLoader } from "react-spinners";
import { TypeAnimation } from "react-type-animation";
import { resetSummary } from "../../../store/slices/ai/ai.slice";

const AIBox = ({ isExpand, areTables }) => {
  const dispatch = useDispatch();
  const { summary, loadingSummary } = useSelector((state) => state.ai);
  const [expand, setExpand] = useState(isExpand && true);

  // Reset summary when component mounts and unmounts
  useEffect(() => {
    dispatch(resetSummary());
    return () => {
      dispatch(resetSummary());
    };
  }, [dispatch]);

  const toggleExpand = () => {
    setExpand((state) => !state);
  };

  const renderTables = () => {
    if (!summary || !areTables) return null;

    // Try to parse summary as JSON if it's a string
    let parsedSummary;
    try {
      if (typeof summary === "string") {
        // Try to extract JSON from the text
        const jsonMatch = summary.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          parsedSummary = JSON.parse(jsonMatch[0]);
        } else {
          parsedSummary = JSON.parse(summary);
        }
      } else {
        parsedSummary = summary;
      }
    } catch (e) {
      // If parsing fails, return the text version
      return (
        <TypeAnimation
          style={{
            whiteSpace: "pre-line",
            display: "block",
            fontSize: "1.6rem",
            lineHeight: "2",
            color: "#333",
          }}
          sequence={[summary]}
          speed={99}
          cursor={false}
        />
      );
    }

    // Check if it's FNB format
    if (parsedSummary.terminal_level_price_trends) {
      const {
        terminal_level_price_trends,
        price_hotspots,
        store_level_pricing_extremes,
      } = parsedSummary;

      if (
        !Array.isArray(terminal_level_price_trends) ||
        !Array.isArray(price_hotspots) ||
        !Array.isArray(store_level_pricing_extremes)
      ) {
        return null;
      }

      return (
        <div>
          <h3>Terminal Level Price Trends</h3>
          <table>
            <thead>
              <tr>
                <th>Terminal</th>
                <th>Avg Price Diff</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {terminal_level_price_trends?.map((item, index) => (
                <tr key={index}>
                  <td>{item.terminal}</td>
                  <td>{item.avg_price_diff}</td>
                  <td>{item.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Price Hotspots</h3>
          <table>
            <thead>
              <tr>
                <th>Terminal Store</th>
                <th>Item</th>
                <th>More Expensive %</th>
              </tr>
            </thead>
            <tbody>
              {price_hotspots?.map((item, index) => (
                <tr key={index}>
                  <td>{item.terminal_store}</td>
                  <td>{item.item}</td>
                  <td>{item.more_expensive_pct}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Store Level Pricing Extremes</h3>
          <table>
            <thead>
              <tr>
                <th>Store</th>
                <th>Avg Price Diff</th>
                <th>Comment</th>
              </tr>
            </thead>
            <tbody>
              {store_level_pricing_extremes?.map((item, index) => (
                <tr key={index}>
                  <td>{item.store}</td>
                  <td>{item.avg_price_diff}</td>
                  <td>{item.comment}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    // Check if it's DF format
    if (parsedSummary.location_category_level_price_trends) {
      const {
        location_category_level_price_trends,
        website_level_pricing_extremes,
        price_hotspots,
      } = parsedSummary;

      if (
        !Array.isArray(location_category_level_price_trends) ||
        !Array.isArray(website_level_pricing_extremes) ||
        !Array.isArray(price_hotspots)
      ) {
        return null;
      }

      return (
        <div>
          <h3>Location Category Level Price Trends</h3>
          <table>
            <thead>
              <tr>
                <th>Location Category</th>
                <th>Avg Price Diff</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {location_category_level_price_trends?.map((item, index) => (
                <tr key={index}>
                  <td>{item.location_category}</td>
                  <td>{item.avg_price_diff}</td>
                  <td>{item.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Website Level Pricing Extremes</h3>
          <table>
            <thead>
              <tr>
                <th>Website</th>
                <th>Avg Price Diff</th>
                <th>Interpretation</th>
              </tr>
            </thead>
            <tbody>
              {website_level_pricing_extremes?.map((item, index) => (
                <tr key={index}>
                  <td>{item.website}</td>
                  <td>{item.avg_price_diff}</td>
                  <td>{item.interpretation}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Price Hotspots</h3>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>More Expensive %</th>
              </tr>
            </thead>
            <tbody>
              {price_hotspots?.map((item, index) => (
                <tr key={index}>
                  <td>{item.product}</td>
                  <td>{item.more_expensive_pct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }

    return null;
  };

  return (
    <div className={styles["wrapper"]}>
      <div
        className={
          expand
            ? styles["heading"]
            : `${styles["heading"]} ${styles["collapse"]}`
        }
      >
        <div>AI Summary</div>
        {expand ? (
          <div onClick={toggleExpand}>
            <i
              className={`bi bi-caret-up-fill`}
              style={{ fontSize: "2rem", cursor: "pointer" }}
            ></i>
          </div>
        ) : (
          <div onClick={toggleExpand}>
            <i
              className={`bi bi-caret-down-fill`}
              style={{ fontSize: "2rem", cursor: "pointer" }}
            ></i>
          </div>
        )}
      </div>

      <div
        className={`${styles["box_wrapper"]} ${
          expand ? styles["open"] : styles["close"]
        }`}
      >
        <div className={styles["box"]}>
          {loadingSummary && <PulseLoader size={10} />}
          {!loadingSummary && summary && areTables ? (
            renderTables()
          ) : (
            <TypeAnimation
              style={{
                whiteSpace: "pre-line",
                display: "block",
                fontSize: "1.6rem",
                lineHeight: "2",
                color: "#333",
              }}
              sequence={[summary]}
              speed={99}
              cursor={false}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AIBox;
